import {
  getModifiedDestinationConfig,
  getSourceType,
  trimConfigStrings,
} from '@components/common/util/util';
import { isOauthEnabledDest } from '@components/common/util/authUtil';
import { validateDestName } from '@components/destinations/destination-view/utils';
import { shouldHideResource } from '@components/directory/utils';
import SourceFactory from '@components/sources/sourceFactory';
import { DestinationConfig, IDestination, AuthStatus } from '@stores/destination';
import { IDestDefinition } from '@stores/destinationDefsList';
import { ISource } from '@stores/source';
import { SourceDefinitionCategory, WarehouseSourceOrigin } from '@stores/types';
import { ISourceDefinition } from '@stores/sourceDefinitionsList';
import { Source } from '@stores/source/types';

export const getNameError = (value: string, isUniqueName: (name: string) => boolean) => {
  const nameError = validateDestName(value);
  const isUnique = isUniqueName(value);
  return isUnique ? nameError : 'Please choose a unique destination name';
};

type DestinationDef = {
  config?: {
    supportedSourceTypes: unknown[];
    preview: unknown;
    syncBehaviours: string[];
    isAudienceSupported: boolean;
  };
};

const areCompatible = (
  destinationDef: DestinationDef,
  sourceDef: Pick<ISourceDefinition, 'name' | 'category'>,
) => {
  const sourceType = getSourceType(sourceDef);
  const typesSupportedByDest = destinationDef?.config?.supportedSourceTypes || [];
  return typesSupportedByDest.includes(sourceType) || false;
};

const isAudienceAsSource = (source: ISource) =>
  source?.config?.origin === WarehouseSourceOrigin.AUDIENCE ||
  source?.config?.origin === WarehouseSourceOrigin.PROFILES_AUDIENCE ||
  source?.config?.origin === WarehouseSourceOrigin.COHORT_AUDIENCE ||
  source?.config?.origin === WarehouseSourceOrigin.SQL_AUDIENCE;

const supportsAudienceAsSource = (destDef: DestinationDef) =>
  !!destDef?.config?.isAudienceSupported;

const isCompatibleWithSourceOrigin = (source: ISource, destDef: DestinationDef) =>
  !isAudienceAsSource(source) || supportsAudienceAsSource(destDef);

const allowedFromDestinationSide = (sourceDef: { category: SourceDefinitionCategory }) =>
  !SourceFactory(sourceDef.category).hideFromAllowedDestConnections();

type Destination = { destinationDefinition: DestinationDef; id: string };

export const existingSourcesForExistingDestination = (
  allSources: ISource[],
  destination: Destination,
) =>
  existingSourcesForNewDestination(allSources, destination.destinationDefinition)
    .filter((source) => !source.destinations.some((dest) => dest.id === destination.id))
    .filter((source) => SourceFactory(source.sourceDef.category).isNewConnectionAllowed(source));

export const existingDestinationsForExistingSource = (allDests: IDestination[], source: ISource) =>
  allDests
    .filter((dest) => areCompatible(dest.destinationDefinition, source.sourceDef))
    .filter((dest) => !dest.sources.some((s) => s.id === source.id))
    .filter((dest) =>
      SourceFactory(source.sourceDef.category).isDestinationAllowedForConnection(dest),
    )
    .filter((dest) => isCompatibleWithSourceOrigin(source, dest.destinationDefinition));

/**
 * True when a new destination of this type can take events from a source of this type: it is out
 * of preview, supports the source type, and syncs the way the source does.
 */
export const canDestinationTypeTakeFromSourceType = (
  destDef: DestinationDef,
  sourceDef: ISourceDefinition,
) =>
  !destDef.config?.preview &&
  areCompatible(destDef, sourceDef) &&
  !SourceFactory(sourceDef.category).hasIncompatibleSyncMode(
    sourceDef.options?.syncBehaviours,
    destDef.config?.syncBehaviours,
  );

export const newDestinationsForExistingSource = <T extends IDestDefinition>(
  allDestDefs: T[],
  source: ISource,
) =>
  allDestDefs
    .filter((dest) => canDestinationTypeTakeFromSourceType(dest, source.sourceDef))
    .filter((destDef) => isCompatibleWithSourceOrigin(source, destDef));

export const existingSourcesForNewDestination = <
  T extends { sourceDef: Pick<ISourceDefinition, 'name' | 'category'>; destinations: unknown[] },
>(
  allSources: T[],
  destinationDef: DestinationDef,
): T[] =>
  allSources
    .filter((source) => areCompatible(destinationDef, source.sourceDef))
    .filter((source) => allowedFromDestinationSide(source.sourceDef))
    .filter((source) => SourceFactory(source.sourceDef.category).isNewConnectionAllowed(source));

export const existingSourcesForDestinationClone = (
  allSources: ISource[],
  destination: { sources: Source[] } | undefined,
) => {
  if (!destination) {
    return [];
  }
  const { sources } = destination;
  const sourceTypes = sources.map((s) => getSourceType(s.sourceDef));
  return allSources
    .filter((s) => sourceTypes.includes(getSourceType(s.sourceDef)))
    .filter((source) => allowedFromDestinationSide(source.sourceDef))
    .filter((source) => SourceFactory(source.sourceDef.category).isNewConnectionAllowed(source));
};

export const newSourcesForExistingDestination = (
  allSourceDefs: ISourceDefinition[],
  destinationDef: DestinationDef,
) =>
  allSourceDefs
    .filter((sourceDef) => areCompatible(destinationDef, sourceDef))
    .filter((sourceDef) => !shouldHideResource(sourceDef))
    .filter(allowedFromDestinationSide);

export const getNewDestinationConfig = (
  config: DestinationConfig,
  destDef: IDestDefinition,
  sources: { sourceDef: Pick<ISourceDefinition, 'name' | 'category'> }[] | undefined,
) => {
  const modifiedConfig = getDestinationConfig(config, destDef, sources);
  if (isOauthEnabledDest(destDef)) {
    modifiedConfig.authStatus = AuthStatus.ACTIVE;
  }
  return modifiedConfig;
};

export const getDestinationConfig = (
  config: DestinationConfig,
  destDef: IDestDefinition,
  sources: { sourceDef: Pick<ISourceDefinition, 'name' | 'category'> }[] | undefined,
) => {
  const trimmedConfig = trimConfigStrings(config);
  return getModifiedDestinationConfig(destDef, trimmedConfig, sources);
};
